import '@fontsource-variable/figtree';
import '@fontsource-variable/fraunces';
import './styles.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './auth/AuthContext';
import { ApiError } from './api/client';
import { ToastProvider } from './ui/kit';
import { outbox } from './offline/outbox';
import { BookingSite } from './pages/site/BookingSite';

const queryClient = new QueryClient({
  defaultOptions: {
    // Never pause just because the browser says it is offline: that flag is unreliable on patchy mobile data, and the
    // outbox needs its requests to run (and fail) so it can keep the change for later.
    mutations: { networkMode: 'always' },
    queries: {
      networkMode: 'always',
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // a client error will not fix itself, so retry only on network trouble
      retry: (count, err) => (err instanceof ApiError && err.status >= 400 && err.status < 500 ? false : count < 1),
    },
  },
});

// The public booking website needs no sign-in, no session refresh and none of the staff app's machinery.
const isPublicSite = window.location.pathname === '/book' || window.location.pathname.startsWith('/book/');
if (!isPublicSite) {
  outbox.attach(queryClient);
  outbox.start();
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {isPublicSite ? (
        <BookingSite />
      ) : (
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);

// Installable app shell. Disabled in development so it never serves stale code.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js'));
}
